import React, { useState, useCallback, useEffect } from "react";
import { Column, Text } from "@amzn/meridian";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  setScannedBarcode,
  addScannedBarcode,
  setBarcodeScanningMode,
} from "src/store/inspectionCaptureSlice";
import DuplicateBarcodeAlertModal from "./DuplicateBarcodeAlertModal";
import UnkownObjectBarcodeAlertModal from "./UnkownObjectBarcodeAlertModal";
import ManualBarcodeEntryModal from "./ManualBarcodeEntryModal";
import "./BarcodeFrame.scss";
import "src/components/UI/cameraOverlay.scss";
import PillButton from "src/components/UI/PillButton";

interface BarcodeFrameProps {
  onBarcodeConfirmed?: () => void;
}

const isMobile = window.matchMedia('(pointer: coarse)').matches;

const BarcodeFrame: React.FC<BarcodeFrameProps> = ({
  onBarcodeConfirmed,
}) => {
  const dispatch = useAppDispatch();

  const { scannedBarcode, scannedBarcodes } = useAppSelector(
    (state) => state.inspectionCapture
  );

  const [isScanning, setIsScanning] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [showUnknownObjectBarcodeAlert, setShowUnknownObjectBarcodeAlert] = useState(false);
  const [duplicateBarcode, setDuplicateBarcode] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);

  const dpr = window.devicePixelRatio || 1;
  const idealWidth = isMobile ? 1280 : Math.floor(window.innerWidth * dpr);
  const idealHeight = isMobile ? 720 : Math.floor(window.innerHeight * dpr);

  const validateAndProcessBarcode = (barcodeValue: string) => {
    dispatch(setScannedBarcode(barcodeValue));

    if (scannedBarcodes.includes(barcodeValue)) {
      setDuplicateBarcode(barcodeValue);
      setShowDuplicateAlert(true);
      return;
    }

    const barcodePrefix = barcodeValue.substring(0, 3).toUpperCase();
    if (barcodePrefix !== "HAZ" && barcodePrefix !== "NHZ") {
      setShowUnknownObjectBarcodeAlert(true);
      return;
    }

    handleConfirm(barcodeValue);
  };

  const handleScan = useCallback(
    (result: any) => {
      if (result && result.length > 0) {
        const barcodeValue = result[0].rawValue;

        if (/^\d+$/.test(barcodeValue)) {
          return;
        }

        validateAndProcessBarcode(barcodeValue);
      }
    },
    []
  );

  const handleError = useCallback((error: any) => {
    console.error("Camera error:", error);
  }, []);

  const handleConfirm = (barcodeValue: string) => {
    if (barcodeValue) {
      dispatch(addScannedBarcode(barcodeValue));
    }
    if (onBarcodeConfirmed) {
      onBarcodeConfirmed();
    } else {
      dispatch(setBarcodeScanningMode(false));
    }
  };

  const handleRescan = () => {
    dispatch(setScannedBarcode(null));
    setShowUnknownObjectBarcodeAlert(false);
  };

  const handleDuplicateAlertClose = () => {
    setShowDuplicateAlert(false);
    setDuplicateBarcode("");
  };

  const handleManualBarcodeSubmit = (barcode: string) => {
    setShowManualEntry(false);
    validateAndProcessBarcode(barcode);
  };

  useEffect(() => {
    setIsScanning(true);
  }, []);

  return (
    <Column className="barcode-frame-viewport pwa-fullscreen-viewport" height="100vh">
      {isScanning && (
        <Scanner
          onScan={handleScan}
          onError={handleError}
          formats={['code_128', 'upc_a', 'ean_13']}
          components={{finder: false}}
          allowMultiple={false}
          constraints={{
            facingMode: "environment",
            width: { ideal: idealWidth },
            height: { ideal: idealHeight },
          }}
          styles={{
            container: {
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            },
            video: {
              width: "100%",
              height: "100%",
              objectFit: "cover",
            },
          }}
        />
      )}

      <div className="barcode-guidance-pill">
        <Column className="camera-pill-bg" width="300px" alignmentHorizontal="center" spacingInset="300">
          <Text alignment="center" color="inverted">Scan the barcode on the top of the container lid</Text>
        </Column>
      </div>

      <div
        style={{
          position: "absolute",
          top: "52%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxWidth: "700px",
          height: "40%",
          border: "3px solid #00A650",
          borderRadius: "12px",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "75%",
          left: "50%",
          transform: "translateX(-50%)",
          textAlign: "center",
        }}
      >
        <Text color="inverted" type="h100" alignment="center">
          {isScanning ? "Position barcode within frame" : "Processing..."}
        </Text>
      </div>

      <UnkownObjectBarcodeAlertModal
        open={showUnknownObjectBarcodeAlert}
        barcode={scannedBarcode || ""}
        onRescan={handleRescan}
      />

      <DuplicateBarcodeAlertModal
        open={showDuplicateAlert}
        barcode={duplicateBarcode}
        onClose={handleDuplicateAlertClose}
      />

      <Column className="camera-overlay camera-overlay--bottom" alignmentHorizontal="center" spacingInset="none none 600">
        <PillButton text="Having Trouble? Try Manual Entry" color="glass" onClick={() => setShowManualEntry(true)} />
      </Column>

      <ManualBarcodeEntryModal
        open={showManualEntry}
        onSubmit={handleManualBarcodeSubmit}
        onClose={() => {
          setShowManualEntry(false);
        }}
      />
    </Column>
  );
};

export default BarcodeFrame;
